import React, { useState, useEffect } from "react";
import {
    Typography,
    Container,
    Grid,
    TextField,
    Button,
    FormControl,
    Box,
    ListItemText,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Snackbar,
    Alert,
    Link,
    Autocomplete,
    Checkbox,
    TextField as MuiTextField,
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import Footer from '../Materials/Footer';
import emailjs from '@emailjs/browser';
import BurgerBar from '../Materials/BurgerBar';

export default function ShareIdeaPage() {
    const [shareType, setShareType] = useState("hub4food"); 
    const [selectedEntity, setSelectedEntity] = useState("");
    const [selectedEmail, setSelectedEmail] = useState([]);
    const [externalName, setExternalName] = useState("");
    const [externalEmail, setExternalEmail] = useState("");
    const [description, setDescription] = useState("");
    const [dontKnowContact, setDontKnowContact] = useState(false);
    const [generatedLink, setGeneratedLink] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    const EMAILJS_SERVICE_ID = 'service_rd9pamh';
    const EMAILJS_TEMPLATE_ID = 'template_goyh1xw';
    const EMAILJS_PUBLIC_KEY = 'LvzARe3Qsdh_JTfsk';

    useEffect(() => {
        if (location.state?.ideaId) {
            console.log("Idea ID:", location.state.ideaId);
        }
    }, [location.state]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetch('https://api.hub4food.colab4food.com/api/users');
                if (!response.ok) {
                    throw new Error('Failed to fetch users');
                }
                const data = await response.json();
                
                const formattedUsers = data.map(user => ({
                    id: user._id || user.id,
                    name: user.name,
                    emails: [user.email]
                }));
                
                setUsers(formattedUsers);
                setLoading(false);
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };
    
        fetchUsers();
    }, []);

    const generateRandomPassword = () => {
        const length = 12;
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+~`|}{[]:;?><,./-=";
        let password = "";
        for (let i = 0; i < length; i++) {
            password += charset.charAt(Math.floor(Math.random() * charset.length));
        }
        return password;
    };

    const createExternalUser = async () => {
        try {
            const password = generateRandomPassword();
            
            // 1. Create user in Hub4Food database (com password)
            const hub4foodResponse = await fetch('https://api.hub4food.colab4food.com/api/users', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: externalName,
                    email: externalEmail,
                    password: password,
                    user_permission_id: "469541de-8507-41ad-98b2-952977a61fdb" // Adicionado este campo
                })
            });
    
            if (!hub4foodResponse.ok) {
                const errorData = await hub4foodResponse.json();
                throw new Error(errorData.message || 'Failed to create user in Hub4Food database');
            }
    
            const hub4foodUser = await hub4foodResponse.json();
            const userId = hub4foodUser._id || hub4foodUser.id;
    
            // 2. Create user in Ideation Platform (sem password)
            const ideationResponse = await fetch('https://api.hub4food.colab4food.com/api/userspi', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: userId, // ID do Hub4Food
                    name: externalName,
                    email: externalEmail
                    // Não é necessário enviar user_permission_id para a plataforma de ideação
                })
            });
    
            if (!ideationResponse.ok) {
                const errorData = await ideationResponse.json();
                throw new Error(errorData.error || 'Failed to create user in Ideation Platform');
            }
    
            return userId;
        } catch (error) {
            console.error("Error creating external user:", error);
            throw error;
        }
    };

    const handleShare = async () => {
        setIsSendingEmail(true);
        
        // Validation for hub4food sharing
        if (shareType === "hub4food" && !dontKnowContact && selectedEmail.length === 0) {
            setSnackbarMessage("Please select at least one email");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
            setIsSendingEmail(false);
            return;
        }
    
        // Validation for external sharing
        if (shareType === "external" && !dontKnowContact) {
            if (!externalName || !externalEmail) {
                setSnackbarMessage("Please provide name and email for external contact");
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
                setIsSendingEmail(false);
                return;
            }
            
            // Email format validation
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(externalEmail)) {
                setSnackbarMessage("Please enter a valid email address");
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
                setIsSendingEmail(false);
                return;
            }
    
            // Check if email already exists in Hub4Food users
            const emailExists = users.some(user => 
                user.emails.some(email => email.toLowerCase() === externalEmail.toLowerCase())
            );
            
            if (emailExists) {
                setSnackbarMessage("This email is already registered in Hub4Food. Please use the 'Hub4Food entities' option to share with this contact.");
                setSnackbarSeverity("error");
                setOpenSnackbar(true);
                setIsSendingEmail(false);
                return;
            }
        }
    
        try {
            if (shareType === "hub4food" && !dontKnowContact) {
                const results = await Promise.allSettled(
                    selectedEmail.map(async (email) => {
                        const user = users.find(u => u.emails.includes(email));
                        if (user && location.state?.ideaId) {
                            try {
                                const response = await fetch('https://api.hub4food.colab4food.com/api/users_idea', {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        user_id: user.id,
                                        idea_id: location.state.ideaId,
                                        subject_share: description,
                                        status: false
                                    })
                                });
    
                                const data = await response.json();
                                if (!response.ok) {
                                    if (response.status === 409) {
                                        return { email, error: data.error };
                                    }
                                    throw new Error(`Failed to share with ${email}`);
                                }
                                return { email, success: true };
                            } catch (error) {
                                return { email, error: error.message };
                            }
                        }
                        return { email, error: 'User not found' };
                    })
                );
    
                const successfulShares = results.filter(r => r.value?.success);
                const failedShares = results.filter(r => r.value?.error);
    
                if (failedShares.length > 0) {
                    const errorMessages = failedShares.map(f => 
                        f.value.error.includes('já foi partilhada') 
                            ? f.value.error 
                            : `The email has been shared with ${f.value.email}. Select another!`
                    );
                    
                    if (successfulShares.length > 0) {
                        setSnackbarMessage([
                            `Idea shared successfully with ${successfulShares.length} contact(s)!`,
                            ...errorMessages
                        ].join('\n'));
                        setTimeout(() => navigate("/listIdea"), 3000);
                    } else {
                        setSnackbarMessage(errorMessages.join('\n'));
                    }
                    setSnackbarSeverity("warning");
                } else {
                    setSnackbarMessage(`Idea shared successfully with ${successfulShares.length} contact(s)!`);
                    setSnackbarSeverity("success");
                    setTimeout(() => navigate("/listIdea"), 3000);
                }
            } 
            else if (shareType === "external" && !dontKnowContact) {
                // First create the external user in both databases
                const userId = await createExternalUser();
                
                // Then share the idea with this user
                const shareResponse = await fetch('https://api.hub4food.colab4food.com/api/users_idea', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        user_id: userId,
                        idea_id: location.state.ideaId,
                        subject_share: description,
                        status: false
                    })
                });
    
                if (!shareResponse.ok) {
                    throw new Error('Failed to share idea with external user');
                }
    
                try {
                    // Send email notification
                    const templateParams = {
                        to_name: externalName,
                        to_email: externalEmail,
                        message: description,
                        from_name: "Hub4Food Team"
                    };
    
                    await emailjs.send(
                        EMAILJS_SERVICE_ID,
                        EMAILJS_TEMPLATE_ID,
                        templateParams,
                        EMAILJS_PUBLIC_KEY
                    );
    
                    setSnackbarMessage("Idea shared successfully! An email with the link has been sent to the external contact.");
                    setSnackbarSeverity("success");
                } catch (emailError) {
                    console.error("Email sending failed:", emailError);
                    
                    setSnackbarMessage([
                        "Idea shared successfully!",
                        "However, the email could not be sent automatically.",
                    ].join('\n'));
                    setSnackbarSeverity("warning");
                }
                
                setTimeout(() => navigate("/listIdea"), 3000);
            } 
            else if (dontKnowContact) {
                setSnackbarMessage("Your request has been sent. Someone from Colab4Food will contact you soon.");
                setSnackbarSeverity("success");
                setTimeout(() => navigate("/listIdea"), 3000);
            }
        
            setOpenSnackbar(true);
            
        } catch (error) {
            console.error("Error sharing idea:", error);
            setSnackbarMessage(error.message);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsSendingEmail(false);
        }
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDrawerToggle = () => {
        setOpenDrawer(!openDrawer);
    };

    const handleNavigation = (path) => {
        navigate(path);
        setOpenDrawer(false);
    };

    const handleExit = () => {
        localStorage.removeItem("user");
        navigate("/");
    };

    const handleEntityChange = (e) => {
        setSelectedEntity(e.target.value);
        setSelectedEmail([]);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <BurgerBar />

            <Container maxWidth="md" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
                <Typography variant="h5" sx={{ mb: 3 }}>
                    Share Idea
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Share with</FormLabel>
                            <RadioGroup
                                row
                                value={shareType}
                                onChange={(e) => {
                                    setShareType(e.target.value);
                                    setDontKnowContact(false);
                                    setGeneratedLink("");
                                }}
                            >
                                <FormControlLabel
                                    value="hub4food"
                                    control={<Radio />}
                                    label="Hub4Food entities"
                                />
                                <FormControlLabel
                                    value="external"
                                    control={<Radio />}
                                    label="External entities"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {shareType === "hub4food" ? (
                        <>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        multiple
                                        options={users.flatMap(user => 
                                            user.emails.map((email, index) => ({
                                                id: `${user.id}-${index}`,
                                                email,
                                                name: user.name
                                            }))
                                        )}
                                        value={selectedEmail.map(email => {
                                            const user = users.find(u => u.emails.includes(email));
                                            return {
                                                id: `${user?.id || ''}-0`,
                                                email,
                                                name: user?.name || ''
                                            };
                                        })}
                                        onChange={(event, newValue) => {
                                            setSelectedEmail(newValue.map(item => item.email));
                                        }}
                                        disableCloseOnSelect
                                        getOptionLabel={(option) => `${option.name} (${option.email})`}
                                        renderOption={(props, option, { selected }) => (
                                            <li {...props}>
                                                <Checkbox checked={selected} />
                                                <ListItemText 
                                                    primary={option.name} 
                                                    secondary={option.email} 
                                                />
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <MuiTextField
                                                {...params}
                                                label="Search and select emails*"
                                                placeholder="Type to search..."
                                                InputProps={{
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        filterOptions={(options, { inputValue }) => {
                                            return options.filter(option =>
                                                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                                                option.email.toLowerCase().includes(inputValue.toLowerCase())
                                            );
                                        }}
                                        disabled={dontKnowContact}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={dontKnowContact}
                                            onChange={(e) => {
                                                setDontKnowContact(e.target.checked);
                                                if (e.target.checked) {
                                                    setSelectedEmail([]);
                                                }
                                            }}
                                            value="dontKnow"
                                        />
                                    }
                                    label="I don't know who to contact (Colab4Food will help)"
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="External contact name*"
                                    fullWidth
                                    value={externalName}
                                    onChange={(e) => setExternalName(e.target.value)}
                                    disabled={dontKnowContact}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    label="External contact email*"
                                    fullWidth
                                    type="email"
                                    value={externalEmail}
                                    onChange={(e) => setExternalEmail(e.target.value)}
                                    disabled={dontKnowContact}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Radio
                                            checked={dontKnowContact}
                                            onChange={(e) => setDontKnowContact(e.target.checked)}
                                            value="dontKnow"
                                        />
                                    }
                                    label="I don't know who to contact (Colab4Food will help)"
                                />
                            </Grid>

                            {generatedLink && !dontKnowContact && (
                                <Grid item xs={12}>
                                    <Typography variant="body1">
                                        Share this link with the external contact:{" "}
                                        <Link href={generatedLink} target="_blank" rel="noopener">
                                            {generatedLink}
                                        </Link>
                                    </Typography>
                                </Grid>
                            )}
                        </>
                    )}

                    <Grid item xs={12}>
                        <TextField
                            label="Include a description for the contact or what type of contact they want, entity and area"
                            fullWidth
                            multiline
                            rows={4}
                            value={description}
                            onChange={(e) => {
                                if (e.target.value.length <= 200) {
                                    setDescription(e.target.value);
                                }
                            }}
                            inputProps={{
                                maxLength: 200,
                            }}
                            helperText={`${description.length}/200 characters`}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleShare}
                            sx={{ mt: 2 }}
                            disabled={!location.state?.ideaId || isSendingEmail}
                        >
                            {isSendingEmail ? "Sharing..." : "Confirm Share"}
                        </Button>
                    </Grid>
                </Grid>
            </Container>

            <Box>
                <Footer />
            </Box>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}